import React from 'react';
import { EmailIcon, UserIdentity } from '@backstage/core-components';
import {
  useApi,
  oktaAuthApiRef,
  errorApiRef,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import loginImage from '../../assets/images/login.png';
import { CompanyLogoDark } from '../../assets/icons';

const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';
const PROVIDER_ID = 'okta-auth-provider';

const OktaSignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const oktaAuthApi = useApi(oktaAuthApiRef);
  const errorApi = useApi(errorApiRef);
  const [loading, setLoading] = React.useState(false);

  const handleOktaLogin = React.useCallback(async () => {
    setLoading(true);
    localStorage.setItem(PROVIDER_STORAGE_KEY, PROVIDER_ID);

    try {
      const identityResponse = await oktaAuthApi.getBackstageIdentity({
        instantPopup: true,
      });

      if (!identityResponse) {
        throw new Error('Okta provider is not configured to support sign-in');
      }

      const profile = await oktaAuthApi.getProfile();

      const userIdentity = UserIdentity.create({
        identity: identityResponse.identity,
        profile,
        authApi: oktaAuthApi,
      });

      const wrappedUserIdentity = new Proxy(userIdentity, {
        get(target, prop) {
          if (prop === 'signOut') {
            return async () => {
              localStorage.removeItem(PROVIDER_STORAGE_KEY);
              sessionStorage.removeItem('backstage.auth.redirectAfterLogin');
              await target.signOut?.();
            };
          }
          return (target as any)[prop];
        },
      });

      // Check if there's a redirect URL stored from ProtectedRoute
      const redirectPath = sessionStorage.getItem(
        'backstage.auth.redirectAfterLogin',
      );
      if (redirectPath) {
        sessionStorage.removeItem('backstage.auth.redirectAfterLogin');
      }

      onSignInSuccess(wrappedUserIdentity);
    } catch (error) {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
      errorApi.post(error as Error);
      setLoading(false);
    }
  }, [oktaAuthApi, errorApi, onSignInSuccess]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="flex flex-col md:flex-row max-w-4xl w-full rounded-lg shadow-lg overflow-hidden bg-white">
        {/* Left Panel - Sign In Form */}
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8 md:p-12">
          <div className="flex flex-col items-center space-y-8">
            {/* Logo and Header */}
            <div className="flex flex-col items-center space-y-6">
              <CompanyLogoDark className="w-16 h-16" />
              <div className="text-center space-y-2">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Property Management Technology
                </h1>
                <p className="text-base text-gray-600">Developer Portal</p>
                <p className="text-sm text-gray-500">Sign in to continue</p>
              </div>
            </div>

            {/* Sign In Button */}
            <div className="w-full">
              <button
                onClick={handleOktaLogin}
                disabled={loading}
                className="flex items-center justify-center gap-3 w-full py-3 px-4 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <EmailIcon />
                    <span>Sign in with Okta</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right Panel - Illustration */}
        <div className="hidden md:flex w-1/2 items-center justify-center bg-gray-100 p-12">
          <img
            src={loginImage}
            alt="Developer Illustration"
            className="w-64 h-64 object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default OktaSignInPage;
